import { validateCameraStation } from './cameraStation.js';
export { cameraEnvelopeFits } from './cameraStation.js';
/** Single-view compositions expand to the same station/view model as shared stations. */
export function createCameraGraph(declarations, paths = []) {
    return {
        stations: Object.fromEntries(Object.entries(declarations).map(([id, entry]) => [id, entry.station])),
        views: Object.fromEntries(Object.entries(declarations).map(([id, entry]) => [id, { station: id, target: entry.target }])),
        paths,
    };
}
export function validateCameraGraph(graph) {
    const ids = new Set();
    for (const [id, station] of Object.entries(graph.stations)) {
        if (!id)
            throw new Error('Camera station IDs must not be empty.');
        validateCameraStation(station);
    }
    for (const [id, view] of Object.entries(graph.views)) {
        if (!id)
            throw new Error('Camera view IDs must not be empty.');
        if (!graph.stations[view.station])
            throw new Error(`Camera view ${id} references a missing station.`);
    }
    for (const path of graph.paths) {
        if (!path.pathId || ids.has(path.pathId))
            throw new Error(`Duplicate or empty camera path ID: ${path.pathId}`);
        ids.add(path.pathId);
        if (path.pathKind !== 'straight')
            throw new Error(`Unsupported camera path kind: ${path.pathKind}`);
        if (!graph.views[path.from] || !graph.views[path.to]) {
            throw new Error(`Camera path ${path.pathId} references a missing view.`);
        }
    }
}
export function deriveCameraGraphNavigationPhase(frame, arrivedView) {
    const camera = frame.spaceRenderOptions?.camera;
    if (camera?.navigationMode !== 'guided' || camera.activeView === undefined)
        return undefined;
    const graph = camera.graph;
    if (!graph)
        throw new Error('An active camera view requires a camera graph.');
    validateCameraGraph(graph);
    const destination = camera.activeView;
    if (!graph.views[destination])
        throw new Error(`Missing active camera view: ${destination}`);
    if (destination === arrivedView)
        return undefined;
    const duration = camera.navigationDurationMs ?? 0;
    if (!Number.isFinite(duration) || duration < 0)
        throw new Error('Camera navigation duration must be finite and nonnegative.');
    const from = arrivedView && graph.views[arrivedView] ? arrivedView : undefined;
    const path = from === undefined ? undefined : graph.paths.find((edge) => edge.from === from && edge.to === destination);
    if (from !== undefined && !path)
        throw new Error(`Missing camera path from ${from} to ${destination}.`);
    return {
        patch: {},
        cameraNavigation: {
            ...(path ? { path: path.pathId } : { view: destination }),
            ...(camera.navigationOrbitCenter ? { orbitCenter: camera.navigationOrbitCenter } : {}),
        },
        transitionOptions: {
            transitionKind: 'interpolated',
            transitionKey: `camera-graph:${from ?? 'live'}:${destination}`,
            duration,
            easing: 'smoothstep',
        },
    };
}
